import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 4 Tracking Hook
 * 
 * Tracks page views on route changes for SPA navigation.
 * GA4 Measurement ID: G-L9KG8FLPJC
 * 
 * This hook:
 * - Fires page_view event on every route change
 * - Includes page path, title, and location
 * - Works with client-side routing (React Router)
 */

// GA4 Measurement ID
const GA_MEASUREMENT_ID = 'G-L9KG8FLPJC';

// Helper function to send events to GA4
export const sendGAEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// Helper function to send page view
export const sendPageView = (path, title) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href
    });
  }
};

/**
 * useGoogleAnalytics Hook
 * 
 * Call this hook once in your app (e.g., in App.js or a layout component)
 * to automatically track page views on route changes.
 */
const useGoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Send page view on route change
    const path = location.pathname + location.search;
    const title = document.title;
    
    // Small delay to ensure page title is updated (especially with react-helmet)
    const timeoutId = setTimeout(() => {
      sendPageView(path, document.title || title);
      
      // Debug log (remove in production if desired)
      if (process.env.NODE_ENV === 'development') {
        console.log(`[GA4] Page view: ${path}`);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);
};

/**
 * GoogleAnalytics Component
 * 
 * Alternative to the hook - use as a component in your router.
 * Place after BrowserRouter but before Routes.
 */
const GoogleAnalytics = () => {
  useGoogleAnalytics();
  return null;
};

export { useGoogleAnalytics };
export default GoogleAnalytics;
